/**
 * The canvas-mutating half of the cockpit: replays a baked composition's draw
 * ops into a **1:1 320x200** offscreen canvas — the same frame buffer the
 * original composed into — then presents that canvas with an integer
 * nearest-neighbour upscale (`panel-layout.ts`'s `presentTransform`).
 *
 * Nothing here is scaled, stretched or repositioned to fit the window: the
 * ops carry the original's own coordinates and are drawn at them. Everything
 * this file consumes (`PresentTransform`, `CockpitGaugeSlot`,
 * `InstrumentReadings`) comes from a pure, tested module, which is why the
 * file itself is untested — `bun:test` has no canvas, the same reasoning
 * `assets/waveform.ts`'s `drawPeaks` documents next to its tested
 * `computePeaks`.
 *
 * Two fidelity notes, both deliberate:
 * - Filled polygons are **filled and stroked** in the same colour. The
 *   original's `SET DRAW COLORS` op carries a fill *and* an outline colour
 *   (always equal in all 407 occurrences across the 22 cockpit scripts), and
 *   stroking also closes the half-pixel anti-aliased seams the canvas leaves
 *   between abutting polygons.
 * - Palette index 0 is transparent (the Dynamix sprite convention, already
 *   baked as alpha 0), so the 3D viewport shows through the sky gap exactly
 *   where the original's does.
 */

import type { CockpitComposition, CockpitDrawOp, CockpitGaugeSlot, SpriteFrameMeta } from "@red-opus/baker/schema";
import { compassCardOffsetPx, type InstrumentReadings } from "./instrument-values.ts";
import { compassWindowRect, LOGICAL_HEIGHT, LOGICAL_WIDTH, type PresentTransform, type Rect } from "./panel-layout.ts";

/** The instrument sheet `RB.EXE` composites over the TTM-drawn panel; the compositions carry its frame rects under this key. */
const GAUGES_SHEET = "GAUGES.BMP";
/** `GAUGES.BMP` frame 6 — the lettered compass tape scrolled through the bezel's window. */
const COMPASS_CARD_FRAME = 6;
const NEEDLE_COLOR = "#141008";
/**
 * The altimeter is the one black-faced dial ("black-faced round dial, two
 * hands" — ui-reference, confirmed live), so the shared near-black needle
 * vanished on it (user, 2026-08-23). Its hands draw in the moon-grey the
 * palette already uses for light instrument marks (`BARON.PAL` 199).
 */
const ALTIMETER_HAND_COLOR = "#cbcbcb";
const POINTER_COLOR = "#e6c85a";
const COMPASS_WINDOW_BACKING = "#0c0a08";

export interface CockpitArt {
  readonly composition: CockpitComposition;
  readonly colors: readonly string[];
  readonly sheets: ReadonlyMap<string, HTMLImageElement>;
}

function frameRect(art: CockpitArt, sheet: string, index: number): SpriteFrameMeta | undefined {
  return art.composition.sheetFrames[sheet]?.[index];
}

function colorOf(art: CockpitArt, index: number): string {
  return art.colors[index] ?? "#ff00ff";
}

function drawSprite(ctx: CanvasRenderingContext2D, art: CockpitArt, op: Extract<CockpitDrawOp, { kind: "sprite" }>): void {
  const image = art.sheets.get(op.sheet);
  const frame = frameRect(art, op.sheet, op.frame);
  if (!image || !frame) return;
  const scaleX = op.flipX ? -1 : 1;
  const scaleY = op.flipY ? -1 : 1;
  ctx.save();
  ctx.translate(op.x + (op.flipX ? frame.width : 0), op.y + (op.flipY ? frame.height : 0));
  ctx.scale(scaleX, scaleY);
  ctx.drawImage(image, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
  ctx.restore();
}

function tracePolygon(ctx: CanvasRenderingContext2D, points: readonly { x: number; y: number }[]): void {
  ctx.beginPath();
  points.forEach((point, index) => {
    if (index === 0) ctx.moveTo(point.x + 0.5, point.y + 0.5);
    else ctx.lineTo(point.x + 0.5, point.y + 0.5);
  });
  ctx.closePath();
}

function drawVectorOp(ctx: CanvasRenderingContext2D, art: CockpitArt, op: CockpitDrawOp): void {
  const color = colorOf(art, "color" in op ? op.color : 0);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  switch (op.kind) {
    case "poly":
      if (op.points.length === 0) return;
      tracePolygon(ctx, op.points);
      if (op.filled) ctx.fill();
      ctx.stroke();
      return;
    case "line":
      ctx.beginPath();
      ctx.moveTo(op.x0 + 0.5, op.y0 + 0.5);
      ctx.lineTo(op.x1 + 0.5, op.y1 + 0.5);
      ctx.stroke();
      return;
    case "rect":
      if (op.filled) ctx.fillRect(op.x, op.y, op.width, op.height);
      else ctx.strokeRect(op.x + 0.5, op.y + 0.5, op.width, op.height);
      return;
    case "circle":
      ctx.beginPath();
      ctx.ellipse(op.x + 0.5, op.y + 0.5, op.radiusX, op.radiusY, 0, 0, Math.PI * 2);
      if (op.filled) ctx.fill();
      ctx.stroke();
      return;
    case "pixel":
      ctx.fillRect(op.x, op.y, 1, 1);
      return;
    default:
      return;
  }
}

/**
 * Replays an op list into the context as it stands, in the original's own
 * execution order — what an overlay drawn *over* a finished picture needs (the
 * being-hit splashes, `cockpit-hits.ts`). `drawComposition` is this plus the
 * clear a whole view starts with.
 */
export function drawOps(ctx: CanvasRenderingContext2D, art: CockpitArt, ops: readonly CockpitDrawOp[]): void {
  ctx.imageSmoothingEnabled = false;
  for (const op of ops) {
    if (op.kind === "sprite") drawSprite(ctx, art, op);
    else drawVectorOp(ctx, art, op);
  }
}

/** Replays one variant's op list into a cleared 320x200 context, in the original's own execution order. */
export function drawComposition(ctx: CanvasRenderingContext2D, art: CockpitArt, ops: readonly CockpitDrawOp[]): void {
  ctx.clearRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
  drawOps(ctx, art, ops);
}

/** A round-dial needle: points straight up at 0 deg, `angleDeg` clockwise from there. `reach` scales the hand's length — the altimeter's short thousands hand passes 0.45. */
function drawRoundNeedle(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  angleDeg: number,
  reach = 0.72,
  color = NEEDLE_COLOR,
): void {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const radius = Math.min(rect.width, rect.height) / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((angleDeg * Math.PI) / 180);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = "butt";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, radius * 0.18);
  ctx.lineTo(0, -radius * reach);
  ctx.stroke();
  ctx.fillRect(-1, -1, 2, 2);
  ctx.restore();
}

/** The fuel sight-tube's chevron, sliding along the ribbon's width. */
function drawFuelPointer(ctx: CanvasRenderingContext2D, rect: Rect, fraction: number): void {
  const x = rect.x + 2 + (rect.width - 4) * fraction;
  ctx.save();
  ctx.fillStyle = POINTER_COLOR;
  ctx.beginPath();
  ctx.moveTo(x, rect.y + rect.height * 0.65);
  ctx.lineTo(x - 2.5, rect.y + rect.height * 0.15);
  ctx.lineTo(x + 2.5, rect.y + rect.height * 0.15);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** Wrapping crop of `GAUGES.BMP` frame 6 through the bezel's measured window, centred on the current heading. */
function drawCompassCard(ctx: CanvasRenderingContext2D, art: CockpitArt, bezel: CockpitGaugeSlot, headingDeg: number): void {
  const card = frameRect(art, GAUGES_SHEET, COMPASS_CARD_FRAME);
  const image = art.sheets.get(GAUGES_SHEET);
  if (!card || !image) return;
  const window = compassWindowRect(bezel);
  const centre = compassCardOffsetPx(headingDeg, card.width);
  let srcX = (((centre - window.width / 2) % card.width) + card.width) % card.width;
  let remaining = window.width;
  let destX = window.x;

  ctx.save();
  ctx.beginPath();
  ctx.rect(window.x, window.y, window.width, window.height);
  ctx.clip();
  ctx.fillStyle = COMPASS_WINDOW_BACKING;
  ctx.fillRect(window.x, window.y, window.width, window.height);
  while (remaining > 0.01) {
    const chunk = Math.min(remaining, card.width - srcX);
    ctx.drawImage(image, card.x + srcX, card.y, chunk, card.height, destX, window.y, chunk, window.height);
    remaining -= chunk;
    destX += chunk;
    srcX = 0;
  }
  ctx.restore();

  ctx.save();
  ctx.fillStyle = POINTER_COLOR;
  ctx.fillRect(window.x + Math.floor(window.width / 2), window.y, 1, window.height);
  ctx.restore();
}

function drawGaugeNeedle(
  ctx: CanvasRenderingContext2D,
  art: CockpitArt,
  slot: CockpitGaugeSlot,
  readings: InstrumentReadings,
): void {
  switch (slot.role) {
    case "altimeter":
      // Two hands, like the original's own dial: short = thousands, long = hundreds.
      drawRoundNeedle(ctx, slot, readings.altimeterDeg, 0.45, ALTIMETER_HAND_COLOR);
      drawRoundNeedle(ctx, slot, readings.altimeterHundredsDeg, 0.72, ALTIMETER_HAND_COLOR);
      return;
    case "airspeed":
      drawRoundNeedle(ctx, slot, readings.airspeedDeg);
      return;
    case "oil":
      drawRoundNeedle(ctx, slot, readings.oilDeg);
      return;
    case "tachometer":
      drawRoundNeedle(ctx, slot, readings.tachometerDeg);
      return;
    case "turnIndicator":
      drawRoundNeedle(ctx, slot, readings.turnIndicatorDeg);
      return;
    case "fuel":
      drawFuelPointer(ctx, slot, readings.fuelFraction);
      return;
    case "compass":
      drawCompassCard(ctx, art, slot, readings.headingDeg);
      return;
    default:
      return;
  }
}

/** The instrument row: each bezel's baked art at its recovered position, then its live needle. */
export function drawGauges(
  ctx: CanvasRenderingContext2D,
  art: CockpitArt,
  gauges: readonly CockpitGaugeSlot[],
  readings: InstrumentReadings,
): void {
  const image = art.sheets.get(GAUGES_SHEET);
  for (const slot of gauges) {
    const frame = frameRect(art, GAUGES_SHEET, slot.frame);
    if (image && frame) {
      ctx.drawImage(image, frame.x, frame.y, frame.width, frame.height, slot.x, slot.y, frame.width, frame.height);
    }
    drawGaugeNeedle(ctx, art, slot, readings);
  }
}

/** Blits the composed 320x200 frame onto the visible canvas, nearest-neighbour, at `transform`'s rect. */
export function presentCockpit(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  canvasWidth: number,
  canvasHeight: number,
  transform: PresentTransform,
): void {
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  ctx.imageSmoothingEnabled = false;
  const { rect } = transform;
  ctx.drawImage(source, 0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, rect.x, rect.y, rect.width, rect.height);
}
